import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List as ListIcon,
  ListOrdered,
  Undo2,
  Redo2,
  Braces,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AVAILABLE_PLACEHOLDERS } from "@/lib/placeholders";

/**
 * RichTextEditor — editor sencillo basado en Tiptap.
 *
 * Soporta bold, italic, hyperlinks, listas y placeholders (chips {{campo}}).
 * NO expone HTML crudo al usuario; solo formato visible.
 *
 * Props:
 *   value          HTML string (persistido en DB)
 *   onChange(html) callback con el HTML editado
 *   placeholder    texto vacío
 *   placeholders   array opcional de {key,label} extra para dropdown de inserción
 *   minRows        alto mínimo (rows)
 *   testId         data-testid
 */
export function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Escribe aquí…",
  placeholders,
  minRows = 8,
  testId = "rich-text-editor",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-primary underline", target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none text-sm text-foreground min-h-[8rem] px-3 py-2",
        style: `min-height: ${minRows * 1.5}rem`,
        "data-testid": `${testId}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Tiptap devuelve "<p></p>" para vacío — convertir a string vacío
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sincronizar cuando el value cambia externamente (ej. Restaurar plantilla)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current && (value || "") !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prevUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace (deja vacío para quitar):", prevUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertPlaceholder = useCallback((key) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${key}}}`).run();
  }, [editor]);

  if (!editor) return null;

  const list = placeholders || AVAILABLE_PLACEHOLDERS;

  return (
    <div className="rounded-md border bg-background focus-within:ring-1 focus-within:ring-ring" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
        <ToolbarBtn on={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Negrita (Ctrl+B)" testId={`${testId}-bold`}>
          <BoldIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn on={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Cursiva (Ctrl+I)" testId={`${testId}-italic`}>
          <ItalicIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn on={editor.isActive("link")} onClick={setLink} label="Enlace (Ctrl+K)" testId={`${testId}-link`}>
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <div className="mx-1 h-4 w-px bg-border" />
        <ToolbarBtn on={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Lista" testId={`${testId}-bullet`}>
          <ListIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn on={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Lista numerada" testId={`${testId}-ordered`}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <div className="mx-1 h-4 w-px bg-border" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" data-testid={`${testId}-insert-placeholder`}>
              <Braces className="h-3.5 w-3.5" /> Insertar dato
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
            <DropdownMenuLabel className="text-xs">Datos de la reserva</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {list.map((p) => (
              <DropdownMenuItem key={p.key} onClick={() => insertPlaceholder(p.key)} className="text-xs" data-testid={`${testId}-ph-${p.key}`}>
                <span className="font-mono text-primary">{`{{${p.key}}}`}</span>
                <span className="ml-2 text-muted-foreground">{p.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} label="Deshacer" testId={`${testId}-undo`}>
            <Undo2 className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} label="Rehacer" testId={`${testId}-redo`}>
            <Redo2 className="h-3.5 w-3.5" />
          </ToolbarBtn>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({ on, onClick, label, testId, children }) {
  return (
    <Button
      type="button"
      variant={on ? "secondary" : "ghost"}
      size="sm"
      className="h-7 w-7 p-0"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      data-active={on ? "true" : "false"}
    >
      {children}
    </Button>
  );
}
